import { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'

import Btn from '../../../../../../UI/Btn'

import SettingHint from '../SettingHint'

import http from '../../../../../../shared/http'
import { TApi } from '../../../../../../shared/const'
import { askConfirm } from '../../../../../../shared/confirmDialog'
import { MAILHOG_URL, isTestEnvironment } from '../../../../../../shared/config'
import { formatVienna } from '../../../../../../shared/datetime'
import { DISPATCH_TEST, TEST_WINDOW_CHOICES, readEmailDispatch } from '../../../../../../shared/emailDispatch'

import styles from '../style.module.scss'

/*
 * Der Versandschalter: ein Testfenster von 30 bis 90 Sekunden.
 *
 * Bis zur Produktivsetzung am 31.08.2026 liess sich der Versand nur am Server
 * umbiegen — EMAIL_HOST in der Compose-Datei ändern und den Container neu
 * starten. Der Umstellungstag brauchte genau das zweimal: einmal zum
 * Stilllegen, einmal zum Scharfstellen danach.
 *
 * Hier steht dasselbe als Knopf. Während des Fensters gehen alle Mails an den
 * Auffangdienst statt hinaus — damit lässt sich eine Handlung auslösen und
 * nachsehen, was für eine Mail dabei entstanden wäre, ohne dass sie jemanden
 * erreicht.
 *
 * Der Rückweg ist bewusst kein zweiter Knopf, sondern ein Zeitpunkt: In der
 * Einstellung steht, bis wann das Fenster gilt. Ist er vorbei, versendet die
 * Anwendung wieder normal — auch nach einem Neustart, auch wenn niemand mehr
 * hinsieht. Ein Testbetrieb, der versehentlich stehen bleibt, ist damit
 * ausgeschlossen. Der Knopf „jetzt beenden" ist nur die Abkürzung für
 * Ungeduldige.
 *
 * Die Sekundenzahl schickt die Ansicht, den Endzeitpunkt rechnet der Server:
 * Ginge die Uhr dieses Rechners nach, wäre das Fenster beim Anlegen schon
 * abgelaufen.
 */

function EmailDispatch(props) {
	const [läuft, setLäuft] = useState(false)
	const [ziel, setZiel] = useState(null)
	/*
	 * Solange die Auskunft unterwegs ist, steht noch keine fest: Ohne diesen
	 * Zustand zeigte der Abschnitt für einen Augenblick „nicht erreichbar" und
	 * wechselte danach zum Verweis. Auf einer langsamen Leitung liest man dann
	 * genau die falsche Auskunft.
	 */
	const [holt, setHolt] = useState(true)

	// Eine eigene Uhr, damit die Restzeit heruntergezählt wird. Sie tickt nur,
	// solange ein Fenster offen ist — eine Sekundenuhr, die den ganzen Tag im
	// Hintergrund läuft, wäre der Preis für nichts.
	const [takt, setTakt] = useState(() => Date.now())
	const stand = useMemo(() => readEmailDispatch(props.form, new Date(takt)), [props.form, takt])

	useEffect(() => {
		if (stand.mode !== DISPATCH_TEST) return undefined
		const uhr = setInterval(() => setTakt(Date.now()), 1000)
		return () => clearInterval(uhr)
	}, [stand.mode])

	/*
	 * Wohin die Mails im Testfenster gehen und wo man sie ansieht. Beides kommt
	 * vom Server und nicht aus dem Quelltext: Es unterscheidet sich je Umgebung,
	 * und der Zugang kann Anmeldedaten tragen. Die Route liegt hinter dem
	 * Admin-Waechter — in den oeffentlichen Einstellungen haette so etwas nichts
	 * verloren.
	 */
	const holeZiel = useCallback(() => {
		setHolt(true)
		http.get(TApi.ADMIN_EMAIL_TEST)
			.then(({ data }) => setZiel(data))
			.catch(() => setZiel(null))
			.finally(() => setHolt(false))
	}, [])

	useEffect(() => {
		holeZiel()
	}, [holeZiel])

	const starte = async sekunden => {
		const weiter = await askConfirm({
			title: `Testbetrieb für ${sekunden} Sekunden starten?`,
			text:
				'In dieser Zeit geht KEINE Mail an ihren Empfänger — auch keine Anmeldebestätigung und keine ' +
				'Passwort-Zurücksetzung, die jemand anderes in diesen Sekunden auslöst. Alles landet beim ' +
				'Auffangdienst. Danach versendet die Anwendung von selbst wieder normal.',
			confirmLabel: 'Testbetrieb starten',
			cancelLabel: 'Abbrechen',
			tone: 'danger'
		})
		if (!weiter) return

		setLäuft(true)
		http.put(TApi.ADMIN_EMAIL_TEST, { seconds: sekunden })
			.then(({ data }) => {
				props.onOptions(data)
				setTakt(Date.now())
				toast.success(`Testbetrieb läuft — ${sekunden} Sekunden`)
			})
			.finally(() => setLäuft(false))
	}

	const beende = () => {
		setLäuft(true)
		http.delete(TApi.ADMIN_EMAIL_TEST)
			.then(({ data }) => {
				props.onOptions(data)
				setTakt(Date.now())
				toast.success('Wieder Produktivversand')
			})
			.finally(() => setLäuft(false))
	}

	const imTest = stand.mode === DISPATCH_TEST

	/*
	 * Der Weg zur Oberfläche des Auffangdienstes. Erste Wahl ist, was der Server
	 * meldet; auf der Testumgebung gibt es die Adresse ohnehin schon — dieselbe,
	 * die der Hinweisstreifen oben zeigt (shared/config). Auf der Produktion
	 * bleibt beides leer: Dort hängt der Auffangdienst bewusst nur am Server.
	 */
	const istTest = isTestEnvironment()
	const zugang = ziel?.viewerUrl || (istTest ? MAILHOG_URL : null)

	/*
	 * Ist die Oberfläche nicht von außen erreichbar, kommt vom Server der Weg
	 * dorthin mit: der Befehl für den Tunnel und die Adresse, unter der sie
	 * danach steht. Bis 01.09.2026 endete die Auskunft beim Wort „SSH-Tunnel" —
	 * wer ihn nicht auswendig konnte, kam nicht weiter.
	 *
	 * `zugang` geht vor: Auf der Testumgebung steht die Oberfläche offen, dort
	 * wäre eine Tunnel-Anleitung ein Umweg um eine offene Tür. Und `viewerTunnel`
	 * kann fehlen — Frontend und Backend werden getrennt ausgeliefert, gegen ein
	 * älteres Backend bleibt es beim Satz von vorher.
	 */
	const tunnel = (!zugang && ziel?.viewerTunnel) || null

	/*
	 * Die Anmeldung am Auffangdienst kommt vom Server und steht nicht mehr im
	 * Quelltext: Das Bundle ist ohne Anmeldung abrufbar, diese Auskunft liegt
	 * hinter dem Admin-Waechter (shared/config hält den Vorgang fest). Fehlt sie,
	 * steht nur der Verweis da — der Browser fragt dann selbst nach.
	 */
	const anmeldung = ziel?.viewerCredentials || null

	/*
	 * Teilen sich beide Umgebungen ein Postfach? Seit 02.09.2026 möglich: Der
	 * Testbetrieb der Produktion kann auf den Auffangdienst der Testumgebung
	 * zeigen, dessen Oberfläche erreichbar ist. Dann stimmt der Satz von den
	 * „zwei verschiedenen Auffangdiensten" nicht mehr — und wer danach sucht,
	 * sucht am falschen Ort. Der Server sagt es; die Ansicht rechnet es nicht
	 * aus der Adresse zusammen.
	 */
	const geteilt = ziel?.viewerShared === true

	const kopiereBefehl = async () => {
		try {
			await navigator.clipboard.writeText(tunnel.command)
			toast.success('Befehl kopiert')
		} catch {
			// Ohne Zwischenablage (älterer Browser, kein sicherer Kontext) bleibt
			// der Befehl sichtbar daneben stehen — er ist nicht verloren.
			toast.info('Kopieren ging nicht — bitte den Befehl von Hand markieren.')
		}
	}

	return (
		<div id="emailDispatch" className={styles.root}>
			<section className={styles.section}>
				<h3 className={styles.sectionTitle}>E-Mail-Versand</h3>
				<SettingHint
					example="Beispiel: Testbetrieb starten, dann eine Schüler:in einladen — die Einladung landet beim Auffangdienst und lässt sich dort ansehen, statt bei der Schüler:in."
					warning="Das Fenster gilt für die ganze Anwendung, nicht nur für Ihre eigene Handlung. Was in diesen Sekunden sonst noch an Mail anfällt — eine Registrierung, eine Passwort-Zurücksetzung —, geht ebenfalls an den Auffangdienst und erreicht niemanden. Deshalb kurz halten und am besten außerhalb der Schulzeit testen."
				>
					Wohin die Anwendung ihre E-Mails schickt. Im Normalfall an den echten Mailserver. Für eine Probe
					lässt sich der Versand für 30 bis 90 Sekunden auf den Auffangdienst umlegen; danach stellt er sich
					von selbst zurück — dafür braucht es keinen zweiten Handgriff und keinen laufenden Auftrag. Welche
					einzelne Vorlage überhaupt versendet wird, steht weiterhin im Menüpunkt „Kommunikation“.
				</SettingHint>
				<div className={styles.sectionContent}>
					{imTest ? (
						<p className={styles.dispatchTest} role="status">
							Testbetrieb läuft — noch {stand.secondsLeft} Sekunden. Alle Mails gehen an den Auffangdienst
							und erreichen niemanden. Ende um {formatVienna(stand.testUntil, 'HH:mm:ss')} Uhr.
						</p>
					) : (
						<p className={styles.hintLine}>Produktivversand: E-Mails gehen an ihre Empfänger:innen.</p>
					)}

					<div className={styles.btnContainer}>
						{imTest ? (
							<Btn className={styles.btn} onClick={beende} disabled={läuft} theme="danger">
								Jetzt beenden
							</Btn>
						) : (
							TEST_WINDOW_CHOICES.map(sekunden => (
								<Btn
									key={sekunden}
									className={styles.btn}
									onClick={() => starte(sekunden)}
									disabled={läuft}
									theme="secondary"
								>
									{`Testbetrieb ${sekunden} s`}
								</Btn>
							))
						)}
					</div>

					{/*
					 * Der Auffangdienst heißt MailHog — in beiden Umgebungen. Der Name
					 * stand bis 01.09.2026 nirgends auf der Seite: Der Verweis hieß
					 * „Auffangdienst öffnen“ und lief im Fließtext mit. Wer MailHog
					 * suchte, fand hier nichts und hielt den Verweis für fehlend.
					 * Deshalb steht der Name jetzt in der Überschrift und am Verweis.
					 */}
					<h4 className={styles.miniSectionTitle}>Der Auffangdienst (MailHog)</h4>
					{/*
					 * Die Adresse steht hier, weil jede Umgebung ihren eigenen
					 * Auffangdienst hat. Wer den der falschen Umgebung öffnet,
					 * sucht dort vergeblich und hält den Testbetrieb für kaputt.
					 *
					 * Zwei verschiedene Adressen, deshalb zwei Sätze: `target` ist der
					 * Mailserver, der die abgefangenen Mails annimmt („mailhog:1025“ —
					 * nur von innen erreichbar), `zugang` die Oberfläche zum Nachsehen.
					 * Beides zu vermengen hat schon dazu geführt, dass jemand die
					 * SMTP-Adresse im Browser aufzurufen versuchte.
					 */}
					<p className={styles.hintLine}>
						Im Testbetrieb nimmt der Auffangdienst dieser Umgebung die Mails auf{' '}
						<b>{ziel?.target || '—'}</b> entgegen. Ansehen lässt sich, was dort liegt, in der Oberfläche von
						MailHog.
					</p>
					{holt ? (
						<p className={styles.hintLine}>Wird geladen …</p>
					) : zugang ? (
						<p className={styles.mailhogLine}>
							<a className={styles.mailhogLink} href={zugang} target="_blank" rel="noreferrer">
								MailHog öffnen
							</a>
							<span className={styles.mailhogUrl}>{zugang}</span>
							{/*
							 * Die Zugangsdaten stehen daneben und nicht in der
							 * Adresse: Chrome bricht eine von der Seite ausgelöste
							 * Navigation zu einer Adresse mit eingebetteten
							 * Zugangsdaten stillschweigend ab — der Klick bliebe
							 * wirkungslos (shared/config hält das fest).
							 */}
							{anmeldung && <span className={styles.mailhogLogin}>Anmeldung: {anmeldung}</span>}
						</p>
					) : tunnel ? (
						<>
							<p className={styles.hintLine}>
								Die Oberfläche von MailHog ist auf dieser Umgebung <b>nicht von außen erreichbar</b> —
								und das ist Absicht: Im Auffangdienst der Produktion liegen echte Nachrichten an
								Schüler:innen, Lehrkräfte und Unterstützer:innen. Der Weg dorthin führt über einen
								Tunnel, den Sie auf Ihrem Rechner aufmachen:
							</p>
							<div className={styles.tunnelBox}>
								<code className={styles.tunnelCommand}>{tunnel.command}</code>
								<Btn className={styles.tunnelCopy} onClick={kopiereBefehl} theme="secondary">
									Befehl kopieren
								</Btn>
							</div>
							{/*
							 * Der Befehl allein reicht nicht: Auf einem frischen Rechner
							 * endet er mit „command not found". Er braucht zwei Programme
							 * und einen AWS-Zugang — das gehört daneben und nicht in eine
							 * Anleitung, die woanders liegt.
							 */}
							<p className={styles.hintLine}>
								<b>Einmalig nötig dafür:</b> die AWS-Kommandozeile und ihr Session-Manager-Zusatz — auf
								einem Mac mit <code className={styles.inlineCommand}>brew install awscli</code> und{' '}
								<code className={styles.inlineCommand}>brew install --cask session-manager-plugin</code>
								. Dazu ein AWS-Zugang mit dem Recht, eine Sitzung auf dieser Maschine zu öffnen; ohne
								hinterlegte Zugangsdaten meldet der Befehl, dass er keine findet.
							</p>
							<p className={styles.mailhogLine}>
								<a className={styles.mailhogLink} href={tunnel.url} target="_blank" rel="noreferrer">
									MailHog öffnen
								</a>
								<span className={styles.mailhogUrl}>{tunnel.url}</span>
								<span className={styles.mailhogLogin}>erreichbar, solange der Tunnel steht</span>
							</p>
						</>
					) : (
						<p className={styles.hintLine}>
							Die Oberfläche von MailHog ist hier bewusst nicht öffentlich erreichbar — sie steht nur auf
							dem Server selbst und damit über einen Tunnel offen.
						</p>
					)}
					{/*
					 * Der wichtigste Satz des Abschnitts, und er steht bewusst hier
					 * unten — er gilt in jedem der drei Fälle darüber.
					 *
					 * Adresse und Anmeldung stehen nur im Admin-Bereich. Der häufigste
					 * Anlass für eine Probe ist aber genau eine Mail, die man
					 * **abgemeldet** auslöst: „Passwort vergessen", eine Einladung
					 * annehmen, eine Registrierung bestätigen. Wer sich abmeldet, ohne
					 * den Auffangdienst offen zu haben, kommt an beides nicht mehr
					 * heran und muss sich erst wieder anmelden — womit die Probe
					 * hinüber ist, denn das Fenster läuft weiter.
					 */}
					<p className={styles.warnLine}>
						<b>Den Auffangdienst öffnen, bevor Sie sich abmelden.</b> Adresse und Anmeldung stehen nur hier
						im Admin-Bereich. Wer abgemeldet probt — „Passwort vergessen", eine Einladung annehmen —, kommt
						danach nicht mehr an sie heran. Also den Auffangdienst zuerst in einem zweiten Fenster öffnen,
						dann abmelden.
					</p>
					<p className={styles.hintLine}>
						{geteilt ? (
							<>
								<b>Test und Produktion sammeln im selben Postfach.</b> Was Sie hier abfangen, liegt
								neben dem aus der jeweils anderen Umgebung. Wonach Sie suchen, erkennen Sie am Empfänger
								und an der Uhrzeit — die beiden Umgebungen kennen dieselben Adressen nicht.
							</>
						) : (
							<>
								<b>Test und Produktion haben zwei verschiedene Auffangdienste.</b> Was Sie hier
								abfangen, steht nicht im jeweils anderen — eine Probe auf der Produktion ist im
								Auffangdienst der Testumgebung nicht zu finden und umgekehrt.
							</>
						)}
					</p>
					<p className={styles.hintLine}>
						Auf der Produktion ist der Testbetrieb eine <b>Ausnahme auf Zeit</b>, kein Dauerzustand: Im
						Auffangdienst der Produktion sollen keine echten Nachrichten an Schüler:innen, Lehrkräfte oder
						Unterstützer:innen liegen. Genau dafür sind die Fenster auf Sekunden begrenzt — und deshalb
						testet man dort am besten außerhalb der Schulzeit, wenn ohnehin kaum jemand angemeldet ist.
					</p>
				</div>
			</section>
		</div>
	)
}

EmailDispatch.propTypes = {
	form: PropTypes.object.isRequired,
	onOptions: PropTypes.func.isRequired
}

export default EmailDispatch
